/**
 * MemoForm's outer layout: composes MemoFormTitleRow, the content editor,
 * TaskFormMetadataBar, MemoAttributionRow, and MemoFormFooter into the form
 * element. Purely presentational — MemoForm.tsx owns all state, derived
 * values, and handlers, and hands them here as props.
 */

import type { FormEvent, KeyboardEvent, ReactNode, RefObject } from 'react';
import type { DateValue, TimeValue } from 'react-aria-components';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import type { StageAssociationValue } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { cx } from '@/utils/cx';

import { TaskFormMetadataBar } from '../task/TaskFormMetadataBar';
import { InlineToolbarSkeleton } from './InlineToolbarSkeleton';
import { MemoAttributionRow } from './MemoAttributionRow';
import { MemoFormFooter } from './MemoFormFooter';
import { MemoFormTitleRow } from './MemoFormTitleRow';
import { PlateNoteEditor } from './PlateNoteEditor';

export interface MemoFormLayoutProps {
  formRef: RefObject<HTMLFormElement | null>;
  onSubmit: (e: FormEvent) => void;
  isInline: boolean;
  size: 'default' | 'lg';

  showTitleRow: boolean;
  titleInputRef: RefObject<HTMLInputElement | null>;
  showTitleInput: boolean;
  /** The privacy + owner cluster. Rendered in the title row (non-inline) or the attribution row (inline). */
  controls: ReactNode;
  title: string;
  onTitleChange: (value: string) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  submitLock: boolean;
  titlePlaceholder: string;
  mode: 'create' | 'edit';
  showCloseButton: boolean;
  onCancel: () => void;

  content: string;
  onContentChange: (value: string) => void;
  contentPlaceholder: string;
  autoFocusBody: boolean;
  initialCaretPoint?: { x: number; y: number } | null;
  orgId?: number;

  isNote: boolean;
  date: string;
  time: TimeValue | null;
  onDateChange: (value: DateValue | null) => void;
  onTimeChange: (value: TimeValue | null) => void;
  hourCycle: 12 | 24;
  effectiveStageAssociation: string | null;
  onStagePickerChange: (assoc: StageAssociationValue | null) => void;

  /** Inline variant only: the record's attribution line (creator · time). */
  attribution?: ReactNode;

  slotAfterContent?: ReactNode;

  error?: Error | null;
  noteKind: 'note' | 'task';

  submitLabel: string;
  isValid: boolean;
  isSubmitting: boolean;
}

export function MemoFormLayout({
  formRef,
  onSubmit,
  isInline,
  size,
  showTitleRow,
  titleInputRef,
  showTitleInput,
  controls,
  title,
  onTitleChange,
  onKeyDown,
  submitLock,
  titlePlaceholder,
  mode,
  showCloseButton,
  onCancel,
  content,
  onContentChange,
  contentPlaceholder,
  autoFocusBody,
  initialCaretPoint,
  orgId,
  isNote,
  date,
  time,
  onDateChange,
  onTimeChange,
  hourCycle,
  effectiveStageAssociation,
  onStagePickerChange,
  attribution,
  slotAfterContent,
  error,
  noteKind,
  submitLabel,
  isValid,
  isSubmitting,
}: MemoFormLayoutProps) {
  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      className={cx(
        // Chromeless when inline — NoteCard owns the (reserved, edit-only)
        // bordered box on the record slideout; the standalone form owns its
        // own card everywhere else.
        !isInline &&
          'border border-[var(--color-border-secondary)] rounded-lg overflow-hidden',
        size === 'lg' && 'flex flex-col h-full'
      )}
    >
      {showTitleRow && (
        <MemoFormTitleRow
          ref={titleInputRef}
          showTitleInput={showTitleInput}
          controls={isInline ? undefined : controls}
          title={title}
          onTitleChange={onTitleChange}
          onKeyDown={onKeyDown}
          submitLock={submitLock}
          titlePlaceholder={titlePlaceholder}
          mode={mode}
          isInline={isInline}
          size={size}
          showCloseButton={showCloseButton}
          onCancel={onCancel}
        />
      )}

      {/* Content editor. In lg size the editor fills available space via
          flex-1 and forces its children to stretch with [&>div]:h-full so
          PlateNoteEditorImpl's wrapper fills the area. Scroll is owned by
          PlateNoteEditorImpl internally (above its toolbar) so the toolbar
          stays pinned at the bottom and never scrolls out of view. */}
      <div
        className={cx(
          size === 'lg' &&
            'flex-1 min-h-[120px] [&>div]:h-full [&_[contenteditable]]:!min-h-full'
        )}
      >
        <PlateNoteEditor
          value={content}
          onChange={onContentChange}
          onKeyDown={onKeyDown}
          placeholder={contentPlaceholder}
          readOnly={submitLock}
          mentionsEnabled
          richTextEnabled
          variant={isInline ? 'inline' : undefined}
          autoFocus={autoFocusBody}
          initialCaretPoint={isInline ? initialCaretPoint : undefined}
          // Seamless click-to-edit: while the lazy Plate chunk resolves, show
          // the note's READ markdown (same text, same 14px/24px metrics) so the
          // body never blanks, PLUS a skeleton that reserves the formatting
          // toolbar's exact footprint — otherwise the toolbar popped in ~half a
          // second after the body and shoved the content below it down, so
          // entering edit felt laggy. Inline only — the modal editor mounts
          // inside its own already-visible chrome and wants the default (null)
          // fallback.
          fallback={
            isInline ? (
              <>
                <MarkdownContent
                  content={content}
                  className='text-sm text-secondary break-words max-w-none [&_*]:text-inherit'
                  size='compact'
                  orgId={orgId}
                />
                <InlineToolbarSkeleton />
              </>
            ) : undefined
          }
        />
      </div>

      {/* Metadata bar (date + stage) — tasks only. Notes have neither. */}
      {!isNote && (
        <TaskFormMetadataBar
          date={date}
          time={time}
          onDateChange={onDateChange}
          onTimeChange={onTimeChange}
          submitLock={submitLock}
          hourCycle={hourCycle}
          effectiveStageAssociation={effectiveStageAssociation}
          onStagePickerChange={onStagePickerChange}
          mode={mode}
        />
      )}

      {/* Inline (record slideout): the caller's attribution line (creator ·
          time) with the privacy + owner cluster at its end — the same row
          NoteCard renders in read mode, so nothing moves on click-in. */}
      {isInline && (
        <MemoAttributionRow attribution={attribution} controls={controls} />
      )}

      {/* Caller-injected content (e.g. linked-records picker in the modal flow).
          Sits inside the form's bordered chrome so it shares scroll surface and
          visual boundary with the editor above. */}
      {slotAfterContent && (
        <div className='px-3 py-2 border-t border-[var(--color-border-secondary)]'>
          {slotAfterContent}
        </div>
      )}
      {/* Error message */}
      {error && (
        <p
          className={cx(
            'text-xs text-utility-error-600',
            isInline ? 'px-0' : 'px-3'
          )}
        >
          {mode === 'create'
            ? `We couldn't create the ${noteKind}. Try again.`
            : "We couldn't save your changes. Try again."}
        </p>
      )}

      {/* Footer — Save/Cancel. Hidden in the inline (record-slideout) variant,
          which autosaves on change and exits on Esc / click-away, so it needs
          no explicit Save or Cancel button.

          `isValid` gates only on memo substance (a title or body). It does NOT
          gate on task-stage readiness by design: the form receives an
          already-resolved `initialValues.stageEntityId` and cannot tell "still
          resolving" from "genuinely stage-less" (both stay creatable). Stage-
          readiness gating lives at the entry points that own the resolution —
          useOpenTaskCompose (which refuses to open while the stage is loading)
          and the affordances it feeds (QuickDraftMenu, the calendar, the record
          Tasks tab) — so an unstaged task can never be composed before "To Do"
          arrives without duplicating the stage query into this layout file. */}
      {!isInline && (
        <MemoFormFooter
          submitLabel={submitLabel}
          isValid={isValid}
          isSubmitting={isSubmitting}
          onCancel={onCancel}
        />
      )}
    </form>
  );
}
